/** Aggro level tracking with high-water mark, hold timer, and quadratic decay curve. */
import type { EnemyArchetypeConfig } from '../utils/Types';
import type { AIState } from './constants';
import { SEARCH_DECAY_MULTIPLIER, ALERT_AGGRO_FLOOR, AGGRO_DECAY_FLOOR } from './constants';
import { clamp } from '../utils/Math';

/**
 * Manages aggro level, high water mark, hold timer, and decay curve.
 */
export class Aggro {
  private _aggro: number = 0;
  private _aggroHWM: number = 0;
  private _holdTimer: number = 0;

  /** External decay rate multiplier (1.0 = normal, >1.0 = slower decay). Set by Marked passive. */
  decayMultiplier: number = 1.0;

  private readonly config: EnemyArchetypeConfig;

  constructor(config: EnemyArchetypeConfig) {
    this.config = config;
  }

  get aggro(): number { return this._aggro; }
  get aggroHWM(): number { return this._aggroHWM; }
  get holdTimer(): number { return this._holdTimer; }

  /** Zero out all aggro state — aggro, HWM, and hold timer. */
  reset(): void {
    this._aggro = 0;
    this._aggroHWM = 0;
    this._holdTimer = 0;
  }

  /** Set aggro to an exact value (clamped 0–100), updating HWM if higher. */
  setTo(value: number): void {
    this._aggro = clamp(value, 0, 100);
    if (this._aggro > this._aggroHWM) {
      this._aggroHWM = this._aggro;
    }
  }

  /** Additive aggro bump, capped at 100. Does not reset hold timer. */
  raise(amount: number): void {
    this._aggro = clamp(this._aggro + amount, 0, 100);
    if (this._aggro > this._aggroHWM) {
      this._aggroHWM = this._aggro;
    }
  }

  /** Spike aggro to 100% and reset the hold timer. */
  spike(): void {
    this._aggro = 100;
    this._aggroHWM = 100;
    this._holdTimer = this.config.aggroHoldDuration * 1000;
  }

  /**
   * Update aggro for one frame. Builds when LOS, decays when not.
   * Returns log message if hold timer just expired, or null.
   */
  update(hasLOS: boolean, distToPlayer: number, detectionRadius: number, state: AIState, delta: number): string | null {
    const deltaSeconds = delta / 1000;
    let logMsg: string | null = null;

    if (hasLOS && distToPlayer <= detectionRadius) {
      // Re-sighting snap
      if (this._aggro < this._aggroHWM) {
        logMsg = `RE-SIGHT snap aggro ${Math.round(this._aggro)} → HWM ${Math.round(this._aggroHWM)}`;
        this._aggro = this._aggroHWM;
      }

      // Build — faster when closer
      const proximityFactor = 1 - (distToPlayer / detectionRadius);
      this._aggro = clamp(this._aggro + this.config.aggroBuildRate * proximityFactor * deltaSeconds, 0, 100);

      // Track HWM
      if (this._aggro > this._aggroHWM) {
        this._aggroHWM = this._aggro;
      }

      // Start hold at 100
      if (this._aggro >= 100) {
        this._holdTimer = Math.max(this._holdTimer, this.config.aggroHoldDuration * 1000);
      }
    } else {
      // Decay
      if (this._holdTimer > 0) {
        this._holdTimer = Math.max(0, this._holdTimer - delta);
        if (this._holdTimer <= 0) {
          this._aggro = clamp(this._aggro, 0, 99);
          logMsg = `hold expired → aggro kicked to ${Math.round(this._aggro)}, decay begins`;
        }
      } else {
        const aggroNorm = this._aggro / 100;
        const decayCurve = clamp((1 - aggroNorm) * (1 - aggroNorm), AGGRO_DECAY_FLOOR, 1);
        const hwmMultiplier = 1 - (this._aggroHWM / 100 * 0.5);
        const stateMultiplier = state === 'search' ? SEARCH_DECAY_MULTIPLIER : 1;
        const externalMultiplier = this.decayMultiplier;
        this._aggro = clamp(this._aggro - this.config.aggroDecayRate * decayCurve * hwmMultiplier * stateMultiplier * externalMultiplier * deltaSeconds, 0, 100);

        // Alert floor — capped at disengageThreshold to prevent chase↔alert oscillation
        if (state === 'alert') {
          const floor = Math.min(ALERT_AGGRO_FLOOR, this.config.disengageThreshold);
          if (this._aggro < floor) {
            this._aggro = floor;
          }
        }

        // Full decay resets HWM
        if (this._aggro <= 0) {
          this._aggroHWM = 0;
        }
      }
    }

    return logMsg;
  }

}
